// src/types/fnct.types.ts — SCE Issues
// Non-conformités de test (FNC-T) et leurs actions — chantier FNC-T, lot F1.
// Cadrage : docs/chantier-fnct.md.
//
// Saisies et rapport en ANGLAIS (le rapport part chez le client), interface en
// français : les valeurs reprises de la base Excel (« I-Test », « Medium »,
// « Engineering documentation - Process ») sont gardées telles quelles ; seuls
// les libellés de l'appli sont traduits.
//
// Personnes : « Prénom Nom » en TEXTE, pas en colonne Personne. Une colonne
// Personne porte un identifiant propre au site : les listes devant passer
// d'OP_SCE à QM_SCE après validation, un nom se copie tel quel, un identifiant
// non. « C'est moi ? » se décide par `memePersonne` (utils/fnct).

/** Containment & Correction, Root Cause Analysis, Corrective, Preventive, Opportunity. */
export type CategorieAction = 'CO' | 'RCA' | 'CA' | 'PA' | 'OA'

export const CATEGORIES_ACTION: readonly CategorieAction[] = ['CO', 'RCA', 'CA', 'PA', 'OA']

export const LIBELLES_CATEGORIE_ACTION: Record<CategorieAction, { fr: string; en: string }> = {
  CO: { fr: 'Confinement et correction', en: 'Containment & Correction' },
  RCA: { fr: 'Analyse des causes', en: 'Root Cause Analysis' },
  CA: { fr: 'Action corrective', en: 'Corrective Action' },
  PA: { fr: 'Action préventive', en: 'Preventive Action' },
  OA: { fr: "Opportunité d'amélioration", en: 'Opportunity' },
}

/**
 * Statut d'une action, CALCULÉ à partir de ses dates (jamais stocké) — même
 * règle que la colonne « Action status » de la base Excel (utils/fnct).
 */
export type StatutAction =
  | 'a-planifier'
  | 'en-cours'
  | 'reportee'
  | 'en-retard'
  | 'terminee-a-temps'
  | 'terminee-en-retard'

export const LIBELLES_STATUT_ACTION: Record<StatutAction, { fr: string; en: string }> = {
  'a-planifier': { fr: 'À planifier', en: 'To plan' },
  'en-cours': { fr: 'En cours', en: 'Open' },
  reportee: { fr: 'Reportée', en: 'Postponed' },
  'en-retard': { fr: 'En retard', en: 'Over due' },
  'terminee-a-temps': { fr: 'Terminée à temps', en: 'Completed on time' },
  'terminee-en-retard': { fr: 'Terminée en retard', en: 'Completed overdue' },
}

/** Criticité calculée par le RPN (Severity × Frequency × Detectability). */
export type CriticiteRpn = 'Minor' | 'Major' | 'Critical'

/** Date sans heure : `AAAA-MM-JJ`. */
export type Jour = string

export interface ActionFnct {
  /** `FNC-T_2026-58-A01` : N° de la FNC-T + rang de l'action. */
  id: string
  /** Identifiant de la ligne SharePoint (absent avant écriture). */
  itemId?: string
  /** N° de la FNC-T. */
  fnct: string
  /** Vide si la base Excel portait une catégorie inconnue. */
  categorie: CategorieAction | ''
  description: string
  responsable: string
  /** Adresse du responsable (texte, pour Power Automate) ; vide si la personne n'est pas dans l'annuaire. */
  responsableUPN?: string
  service: string
  site: string
  ouverture: Jour | null
  echeance: Jour | null
  echeanceReportee: Jour | null
  cloture: Jour | null
  commentaire: string
}

export interface Fnct {
  /** `FNC-T_2026-58` — attribué une fois, jamais recalculé. */
  numero: string
  itemId?: string
  annee: number
  rang: number
  // ── Enregistrement ──
  enregistrement: Jour | null
  enregistrePar: string
  leader: string
  /** Adresse du leader (texte, pour Power Automate) ; vide si la personne n'est pas dans l'annuaire. */
  leaderUPN?: string
  /** Subject Matter Expert. */
  expert: string
  // ── Quoi ──
  /** Product family (« Hipersep Prochrom 450 »), texte libre de la base. */
  modele: string
  /** Product type : Modular / Customized. */
  typeProduit: string
  numerosSerie: string[]
  /** CVE, parfois un PN ou « STOCK » dans la base : gardé tel quel. */
  cve: string
  /** Problem category (« Engineering documentation - Process »). */
  categorie: string
  /** Affected documents / Impact on already performed tests. */
  documentsImpactes: string
  description: string
  // ── Où, quand, priorité ──
  lieuDetection: string
  dateDetection: Jour | null
  priorite: string
  explicationPriorite: string
  // ── Risque (AQ Opérations) ──
  severite: number | null
  frequence: number | null
  detectabilite: number | null
  /** « (AQ Opérations) » : Not required / Required-New / Required-Ongoing. */
  analyseRisque: string
  // ── Correction, investigation, clôture ──
  clotureCorrection: Jour | null
  pretInvestigation: Jour | null
  clotureInvestigation: Jour | null
  numeroCapa: string
  commentaires: string
  /** NC Legitimacy : Accepted / Not accepted / Goodwill. */
  legitimite: string
  cloture: Jour | null
  // ── Relevé d'écarts P&ID / PPL (14/10, docs/chantier-fnct.md § 13) ──
  /** Écarts relevés, une ligne JSON par écart. Vide hors relevé P&ID / PPL. */
  ecarts?: string
  /**
   * Ouverture réelle : vide tant que la FNC-T est « en relevé » (le N° est pris
   * au premier écart). FNC-T de l'Excel : leur date d'enregistrement.
   */
  ouverture?: Jour | null
}

/**
 * Historique de vie d'une FNC-T (lot F3) : sa propre liste, SCE_FNCT_Historique,
 * pas SCE_Commentaires — une FNC-T peut exister sans problème, et l'historique
 * doit déménager avec les deux autres listes vers QM_SCE.
 */
export type TypeEvenementFnct =
  | 'Création'
  | 'Action ajoutée'
  | 'Action close'
  | 'Action reportée'
  | 'Action modifiée'
  /** Le responsable change, depuis la revue (départ, absence — 22/10). */
  | 'Action réattribuée'
  | 'Risque évalué'
  | 'Modification'
  | 'Clôture'
  | 'Réouverture'
  | 'Rapport'
  /** Relevé d'écarts P&ID / PPL (14/10) : un écart ajouté. */
  | 'Écart relevé'
  /** Un écart du relevé corrigé ou retiré depuis la fiche (22/10). */
  | 'Écart modifié'
  | 'Écart retiré'
  /** Relevé ouvert en FNC-T (étape 4) : Ouverture remplie, actions créées. */
  | 'Ouverture'

export interface EvenementFnct {
  itemId?: string
  /** N° de la FNC-T (Title de la ligne). */
  fnct: string
  /** Date et heure, ISO 8601. */
  date: string
  /** « Prénom Nom » de l'auteur (texte : portable d'un site à l'autre). */
  auteur: string
  type: TypeEvenementFnct
  contenu: string
}
